import { useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';

import PageTemplate from 'modules/shared-components/layout/PageTemplate';
import { TokenCard } from '../components/TokenCard';
import { twMerge } from 'tailwind-merge';
import { useParams } from 'react-router-dom';
import { isValidAddress } from 'helpers/api/web3Utils';
import { formatAddress } from 'modules/ethereum/utils';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { HotContracts } from '../components/HotContracts';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import { NotificationDex } from '../../components/alerts/notification';
import {
  ExpandableCard,
  ExpandableCardTitle,
  SubCardBox,
  SubCardTitle,
} from '../../components/cards';
import { useChainPersistedState } from '../../components/chains/getChains';
import { useEffect } from 'react';

export const LoadingTradingInfo = () => {
  const isDesktop = useMediaQuery(screenGte.small);
  return (
    <ExpandableCard
      as={SubCardBox}
      expandable={isDesktop}
      defaultExpanded={isDesktop}
      size="xs"
    >
      <ExpandableCardTitle
        as={SubCardTitle}
        size="sm"
        className="xxs:text-base xxs:text-dex-white-secondary xxs:font-normal"
      >
        <div
          className="xxs:h-2 xxs:my-4 rounded-full xxs:bg-dex-black-700  w-full"
          style={{ width: '100px' }}
        >
          &nbsp;
        </div>
      </ExpandableCardTitle>
    </ExpandableCard>
  );
};

export default function ContractInfo() {
  const params = useParams<{ chain: Chains; address: string }>();
  const isLarge = useMediaQuery(screenGte.large);
  const [selectedChain, setSelectedChain] = useChainPersistedState(
    Chains.Solana
  );
  useEffect(() => {
    if (
      params.chain !== selectedChain &&
      document.visibilityState === 'visible'
    ) {
      setSelectedChain(params.chain);
    }
  }, [params.chain, selectedChain]);

  const { isLoading, data, error, isFetching } = useQuery({
    queryFn: () => ContractService.getContract(params.chain, params.address),
    queryKey: contractServiceKeys.getContract(params.chain, params.address),
    enabled: isValidAddress(params.address || '', params.chain),
    keepPreviousData: true,
  });

  const isDesktop = useMediaQuery(screenGte.large);

  return (
    <PageTemplate
      pages={
        isDesktop
          ? [
              {
                href: `/dex/research`,
                name: 'Research',
              },
              {
                href: `/dex/research/${params.address}`,
                name:
                  data?.generalInfo.symbol || formatAddress(params.address, 3),
              },
            ]
          : [
              {
                href: '/dex/search',
                name: 'Research',
              },
              {
                href: '/dex/search',
                name:
                  data?.generalInfo.symbol || formatAddress(params.address, 3),
              },
            ]
      }
    >
      {isLarge && <HotContracts />}
      <div className="xxs:flex xxs:flex-row xxs:gap-1 lg:py-1 ">
        <div className="flex flex-col xxs:space-y-2 w-full overflow-hidden">
          {isValidAddress(params.address || '', params.chain) && isLoading && (
            <div className="xxs:space-y-2">
              <div className="w-full xxs:h-[190px] xxs:p-2">
                <div
                  className="xxs:h-2 my-1 rounded-full xxs:bg-dex-black-700 w-full"
                  style={{ width: '40px' }}
                >
                  &nbsp;
                </div>
              </div>
              <div className="xxs:space-y-2 w-full">
                <div className="flex flex-col w-full lg:w-full h-full xxs:gap-2">
                  <div className="flex flex-col lg:flex-row w-full h-full">
                    {[1, 2, 3, 4, 5].map((id) => (
                      <LoadingTradingInfo key={id + 'main'} />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col lg:flex-row w-full h-full">
                  {[1, 2, 3].map((id) => (
                    <LoadingTradingInfo key={id + 'second'} />
                  ))}
                </div>
              </div>
            </div>
          )}
          {params.address && !isValidAddress(params.address, params.chain) && (
            <NotificationDex className="xxs:px-2 lg:px-0" type="error">
              This address looks invalid. Please double check and try again.
            </NotificationDex>
          )}
          {error && (
            <NotificationDex
              className="xxs:px-2 lg:px-0"
              type="error"
              errorMessage={error}
            >
              An error occurred.
            </NotificationDex>
          )}
          <div className={twMerge(isFetching && 'animate-pulse')}>
            {data && isValidAddress(params.address || '', params.chain) && (
              <TokenCard key={data.generalInfo.address} contract={data} />
            )}
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <div className="xxs:flex xxs:flex-col lg:flex-row xxs:mx-2 lg:mx-0 xxs:gap-1 ">
          {data && (
            <div className="pt-1 text-xs lg:col-start-2 lg:text-end xxs:text-dex-white-secondary">
              * For research purposes only. Some contracts are editable,
              settings may change. Not a financial advice, do your own research
              before trading.
            </div>
          )}
        </div>
      </div>
    </PageTemplate>
  );
}
